import React, { useEffect, useMemo, useState } from "react";

import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";

import { IssueDetailQuery } from "../gql/Query.generated";

type IssueType = IssueDetailQuery["getIssue"];
type IssueReferent =
  IssueDetailQuery["getIssue"]["references"][number]["referent"];
type IssueReferenceQueryExplain = Extract<
  IssueReferent,
  { __typename?: "QueryExplain" }
>;
type IssueReferenceQuery = Extract<IssueReferent, { __typename?: "Query" }>;

import { useQueryTuningAutomatedExplainListQuery } from "./gql/Query.generated";
import {
  useQueryTuningAutomatedExplainDetailQuery,
  QueryTuningAutomatedExplainDetailQuery,
} from "./gql/Query.detail.generated";

import Loading from "ui/Loading";
import ExplainGridView from "features/Explain/ExplainGridView";
import {
  QueryTuningInsight,
  combineInsights as combineInsights,
  enrichPlan,
  getAnnotationTitle,
  getInsightDescription,
  getInsightLink,
  insightTypeToAnnotationType,
} from "utils/explain";
import WithNodeSelection, {
  useSetSelectedAnnotationId,
} from "features/Explain/WithExplainNodeSelection";
import {
  WithExplainAspect,
  initialExplainAspect,
} from "features/Explain/WithExplainAspect";
import { AnnotatedPlan } from "types/explain";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import WithExplainPlan from "features/Explain/WithExplainPlan";
import { formatBytes, formatList, formatTimestampShort } from "utils/format";
import moment from "moment";
import classNames from "classnames";

import { Link, useLocation } from "react-router-dom";
import Callout from "ui/Callout";
import ContactSupportLink from "ui/ContactSupportLink";
import ModalContainer from "ui/ModalContainer";
import Grid, { BytesCell, MsCell } from "ui/Grid";
import ExplainFingerprint from "features/Explain/ExplainFingerprint";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faArrowRight,
  faCircle1,
  faCircle2,
  faCircle3,
} from "@fortawesome/pro-solid-svg-icons";
import ExplainSidebarV2 from "features/Explain/ExplainSidebarV2";
import Button from "ui/Button";
import { faCog } from "@fortawesome/pro-light-svg-icons";
import WorkbookDiff from "features/Workbooks/WorkbookDiff";
import { useRoutes } from "utils/routes";
import SQL from "ui/SQL";
import { ExpandToggle } from "ui/Icons";
import Popover from "ui/Popover";
import { QueryAdvisorImpact } from "features/QueryAdvisor/QueryAdvisor";
import SplitButton from "ui/SplitButton";

type PlanForInsightLookup = {
  id: string;
  insights: QueryTuningInsight[];
};
type DetailExplainType =
  QueryTuningAutomatedExplainDetailQuery["getQueryExplain"];
type SummaryPlanType = {
  id: string;
  humanId: string;
  label: string;
  seenAt: number;
  fingerprint: string;
  queryId: string | undefined;
  totCost: number | undefined;
  ioMs: number | undefined;
  totalSharedBlksRead: number | undefined;
  ioBytes: number | undefined;
  runtime: number | undefined;
  insights: QueryTuningInsight[];
};

type SelectedPlanType = SummaryPlanType & {
  plan: AnnotatedPlan;
  insightsWithRewrite: DetailExplainType["insightsWithRewrite"];
  pctOfTotal: number;
};

export default function QueryTuningAutomatedExplain({
  issue,
}: {
  issue: IssueType;
}) {
  const [planSelectionOpen, setPlanSelectionOpen] = useState(false);
  const [focusedInsight, setFocusedInsight] = useState(0);
  const [showPreview, setShowPreview] = useState(true);
  const { plan, plans, insights, workbooks, loading, error } =
    useSelectedPlan(issue);
  const { databaseWorkbook, databaseWorkbooksNew, databaseQueryExplain } =
    useRoutes();

  useEffect(() => {
    if (plan) {
      // When a different plan is selected, make sure that the focused insight exists with that plan
      // Otherwise, switch the focused insight
      const currentPlanInsights = plan.insights.map((i) => `${i.type}-${i.id}`);
      if (!currentPlanInsights.includes(insights[focusedInsight])) {
        const existingInsightIdx = insights.findIndex((type) =>
          currentPlanInsights.includes(type),
        );
        setFocusedInsight(existingInsightIdx);
      }
    }
  }, [plan, plans, focusedInsight, setFocusedInsight, insights]);

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  if (!plan) {
    // It's possible (especially for a resolved issue) that all plans have aged out
    return (
      <Callout variant="warning" className="mb-4">
        The EXPLAIN plans referenced by this issue are no longer available.
        {issue.state !== "resolved" && (
          // This should not happen for unresolved issues
          <>
            {" "}
            Please <ContactSupportLink>contact support</ContactSupportLink>.
          </>
        )}
      </Callout>
    );
  }

  const handleSelectPlan =
    plans.length > 1 ? () => setPlanSelectionOpen(true) : undefined;

  const relatedWorkbook =
    workbooks.find((wb) => {
      return wb.issueExplainId === plan.id;
    }) ??
    workbooks.find((wb) => {
      return wb.issueExplainFingerprint === plan.fingerprint;
    });

  const titles = insights.map((insight: string, idx) => {
    const isFirst = idx === 0;
    const isLast = idx === insights.length - 1;
    const backPlan = isFirst
      ? plan
      : planWithInsightType(plans, plan, insights[idx - 1]);
    const nextPlan = isLast
      ? plan
      : planWithInsightType(plans, plan, insights[idx + 1]);
    const backSearch = new URLSearchParams({
      planId: backPlan.id,
    }).toString();
    const nextSearch = new URLSearchParams({
      planId: nextPlan.id,
    }).toString();
    const type = insight.split("-")[0];

    return (
      <div key={`title-${type}`}>
        {isFirst ? (
          <FontAwesomeIcon icon={faAngleLeft} className="mr-2 opacity-50" />
        ) : (
          <Link
            to={{ search: backSearch }}
            onClick={() => setFocusedInsight(idx - 1)}
          >
            <FontAwesomeIcon icon={faAngleLeft} className="mr-2" />
          </Link>
        )}
        {`Insight ${idx + 1} of ${insights.length}: `}
        {getAnnotationTitle(insightTypeToAnnotationType(type))}
        {isLast ? (
          <FontAwesomeIcon icon={faAngleRight} className="ml-2 opacity-50" />
        ) : (
          <Link
            to={{ search: nextSearch }}
            onClick={() => setFocusedInsight(idx + 1)}
          >
            <FontAwesomeIcon icon={faAngleRight} className="ml-2" />
          </Link>
        )}
      </div>
    );
  });

  const secondaryTitle = (
    <>
      Found in plan:
      <Link
        to={databaseQueryExplain(issue.databaseId, plan.queryId, plan.humanId)}
        className="ml-1"
      >
        <ExplainFingerprint fingerprint={plan.fingerprint} />
      </Link>
    </>
  );

  const rewriteQuery = plan.insightsWithRewrite.find(
    (rq) => `${rq.rewriteType}-${rq.nodeId}` === insights[focusedInsight],
  );
  const queryRewriteAvailable = !!rewriteQuery?.rewriteQuery;
  // Fallback when rewriteQuery.originalQuery is not available (due to rewriteQuery not available)
  const queryText = issue.references.find(
    (ref) => ref.kind === "Query" && ref.referent,
  )?.queryText;
  return (
    <>
      <Panel title={titles[focusedInsight]} secondaryTitle={secondaryTitle}>
        <PanelSection>
          <div>
            <span className="font-bold">Pattern Detected: </span>
            {getInsightDescription(insights[focusedInsight].split("-")[0]).join(
              " ",
            )}
          </div>
          <div className="mt-2">
            <a
              target="_blank"
              rel="noopener"
              href={getInsightLink(insights[focusedInsight].split("-")[0])}
            >
              Learn more
            </a>{" "}
            about this insight.
          </div>
          <div className="grid grid-cols-2 mt-3 border rounded overflow-hidden">
            <div className="p-2 font-medium text-[#606060] flex items-center gap-1">
              <div className="flex-auto text-lg">Current Query</div>
              <div className="">Impact</div>
              <QueryAdvisorImpact pctOfTotal={plan.pctOfTotal} />
            </div>
            <div className="p-2 font-medium text-[#606060] flex items-center border-l gap-2">
              <div className="text-lg">Suggested Query Rewrite</div>
              <div className="flex-auto text-xs">
                {showPreview && queryRewriteAvailable ? (
                  <Button
                    bare
                    variant="link"
                    onClick={() => setShowPreview(false)}
                  >
                    Show Rewrite Steps
                  </Button>
                ) : queryRewriteAvailable ? (
                  <Button
                    bare
                    variant="link"
                    onClick={() => setShowPreview(true)}
                  >
                    Show Preview
                  </Button>
                ) : (
                  <Popover content="Query rewrite preview is not available with this insight">
                    <Button bare disabled className="text-[#999999]">
                      Show Preview
                    </Button>
                  </Popover>
                )}
              </div>
              <div className="font-normal">
                {relatedWorkbook ? (
                  <SplitButton
                    label="View Workbook"
                    to={databaseWorkbook(issue.databaseId, relatedWorkbook.id)}
                    alternatives={[
                      {
                        label: "Create Workbook",
                        to: databaseWorkbooksNew(
                          issue.databaseId,
                          plan.queryId,
                          issue.id,
                          plan.id,
                        ),
                      },
                    ]}
                  />
                ) : (
                  <Link
                    className="btn btn-primary"
                    to={databaseWorkbooksNew(
                      issue.databaseId,
                      plan.queryId,
                      issue.id,
                      plan.id,
                    )}
                  >
                    Create Workbook
                  </Link>
                )}
              </div>
            </div>
            {queryRewriteAvailable && showPreview ? (
              <div className="col-span-2">
                <WorkbookDiff
                  original={rewriteQuery.originalQuery}
                  modified={rewriteQuery.rewriteQuery}
                  options={[
                    {
                      value: "diff-split",
                      label: "Split Diff",
                    },
                  ]}
                  isBaseline
                />
              </div>
            ) : (
              <>
                <div>
                  <WorkbookDiff
                    original=""
                    modified={rewriteQuery?.originalQuery ?? queryText}
                    options={[{ value: "read", label: "" }]}
                    isBaseline
                  />
                </div>
                <div className="border-t border-l">
                  <div className="p-[10px]">
                    <RewriteDescription
                      insight={plan.insights.find(
                        (ins) =>
                          `${ins.type}-${ins.id}` === insights[focusedInsight],
                      )}
                      insightType={insights[focusedInsight]}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </PanelSection>
      </Panel>
      <div className="flex gap-3">
        {planSelectionOpen && (
          <ModalContainer
            title="Select plan to view"
            layout="centered"
            onClose={() => setPlanSelectionOpen(false)}
          >
            <Grid
              pageSize={25}
              data={plans}
              defaultSortBy="runtime"
              columns={[
                {
                  field: "seenAt",
                  header: "Executed At",
                  renderer: ({ rowData, fieldData }) => {
                    const search = new URLSearchParams({
                      planId: rowData.id,
                    }).toString();
                    const isCurrent = rowData.id === plan.id;
                    return (
                      <div className="flex gap-1.5 items-center group">
                        <span title="Current plan">
                          <FontAwesomeIcon
                            className={classNames(
                              !isCurrent && "invisible",
                              "-ml-2 group-hover:visible group-hover:text-link-hover",
                            )}
                            icon={faArrowRight}
                          />
                        </span>
                        <Link
                          to={{ search }}
                          onClick={() => setPlanSelectionOpen(false)}
                        >
                          {formatTimestampShort(moment.unix(fieldData))}
                        </Link>
                      </div>
                    );
                  },
                  width: "minmax(20%,180px)",
                },
                {
                  field: "fingerprint",
                  width: "100px",
                  renderer: ({ fieldData }) => {
                    return <ExplainFingerprint fingerprint={fieldData} />;
                  },
                },
                {
                  field: "runtime",
                  style: "number",
                  nullValue: "n/a",
                  renderer: MsCell,
                  width: "minmax(10%,160px)",
                  defaultSortOrder: "DESC",
                },
                {
                  field: "ioMs",
                  header: "I/O Read Time",
                  style: "number",
                  nullValue: "n/a",
                  renderer: MsCell,
                  width: "minmax(10%,160px)",
                },
                {
                  field: "ioBytes",
                  header: "Read From Disk",
                  style: "number",
                  nullValue: "n/a",
                  renderer: BytesCell,
                  width: "minmax(10%,140px)",
                },
                {
                  field: "insights",
                  width: "3fr",
                  disableSort: true,
                  header: "Insights",
                  renderer: SummarizeInsightsCell,
                },
              ]}
            />
          </ModalContainer>
        )}
        <WithExplainPlan plan={plan}>
          <WithExplainAspect initialAspect={initialExplainAspect(plan.plan)}>
            <WithNodeSelection>
              <Panel
                title="EXPLAIN Plan"
                className="flex"
                secondaryTitle={
                  handleSelectPlan && (
                    <Button
                      onClick={handleSelectPlan}
                      bare
                      className="text-link hover:text-link-hover focus:text-link-focus"
                    >
                      <FontAwesomeIcon icon={faCog} /> Select plan
                    </Button>
                  )
                }
              >
                <div className="flex gap-2">
                  <PanelSection>
                    <ActiveExplain
                      plan={plan.plan}
                      databaseId={issue.databaseId}
                      blockSize={issue.server.blockSize}
                    />
                  </PanelSection>
                  <div className="2xl:min-w-[480px] min-w-[400px] p-4 border-l border-[#e5e7eb]">
                    <ExplainSidebarV2
                      databaseId={issue.databaseId}
                      issueId={issue.id}
                      queryId={plan.queryId}
                      plan={plan}
                      relatedWorkbookId={relatedWorkbook?.id}
                      blockSize={issue.server.blockSize}
                      ioBlocks={plan.totalSharedBlksRead}
                    />
                  </div>
                </div>
              </Panel>
            </WithNodeSelection>
          </WithExplainAspect>
        </WithExplainPlan>
      </div>
    </>
  );
}

function useSelectedPlan(issue: IssueType) {
  const now = useMemo(() => moment(), []);
  const query = issue.references.find(
    (ref) => ref.kind === "Query" && ref.referent,
  ).referent as IssueReferenceQuery;

  const explains = issue.references
    .filter((ref) => ref.kind === "QueryExplain" && ref.referent)
    .map((ref) => {
      const referent = ref.referent as IssueReferenceQueryExplain;
      const details = ref.details as {
        plan_fingerprint: string;
        seen_at: number;
        insights: QueryTuningInsight[];
      };
      return {
        id: referent.id,
        humanId: referent.humanId,
        fp: details.plan_fingerprint,
        name: ref.name,
        path: ref.url,
        seenAt: details.seen_at,
        runtime: referent.runtimeMs,
        insights: details.insights,
      };
    });

  const {
    data: listData,
    loading: listLoading,
    error: listError,
  } = useQueryTuningAutomatedExplainListQuery({
    variables: {
      databaseId: issue.databaseId,
      explainIds: explains.map((explain) => explain.humanId),
      issueId: issue.id,
      startTs: now.clone().subtract(7, "d").unix(),
      endTs: now.unix(),
      queryIds: [query.id],
    },
    // EXPLAIN plans can age out (e.g. for resolved issues). The caller shows a notice in that case.
    skip: explains.length == 0,
  });

  const summaryPlans = listData?.getQueryExplains.map(
    (explain): SummaryPlanType => {
      const issueExplain = explains.find((e) => e.id === explain.id);
      return {
        id: explain.id,
        humanId: explain.humanId,
        label: formatTimestampShort(moment.unix(explain.seenAt)),
        seenAt: explain.seenAt,
        fingerprint: explain.fingerprint,
        queryId: explain.querySample?.queryId,
        totCost: explain.totalCost,
        ioMs: explain.totalBlkReadTime,
        totalSharedBlksRead: explain.totalSharedBlksRead,
        ioBytes: explain.totalSharedBlksRead * issue.server.blockSize,
        runtime: explain.querySample?.runtimeMs,
        insights: issueExplain.insights,
      };
    },
  );

  const slowestSummary = sortBy(summaryPlans, (sp) => -sp.runtime);

  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const planId = params.get("planId") ?? slowestSummary?.[0]?.id;
  const summaryPlan = slowestSummary?.find((sp) => sp.id === planId);

  const {
    data: detailData,
    loading: detailLoading,
    error: detailError,
  } = useQueryTuningAutomatedExplainDetailQuery({
    variables: {
      databaseId: issue.databaseId,
      explainId: summaryPlan?.humanId ?? "",
    },
    skip: !summaryPlan,
  });

  const queryStats = listData?.getQueryStats.find(
    (stat) => stat.id === query.id,
  );
  const detailExplain = detailData?.getQueryExplain;
  const plan = buildSelectedPlan(
    summaryPlan,
    detailExplain,
    queryStats?.pctOfTotal ?? 0,
  );

  const insights = slowestSummary.flatMap((p) => {
    return p.insights.map((i) => `${i.type}-${i.id}`);
  });

  useSetDefaultSelectionOnLoad(plan?.id, plan?.plan);

  return {
    plan,
    plans: slowestSummary,
    insights: [...new Set(insights)],
    workbooks: listData?.getIssueWorkbooks,
    loading: listLoading || detailLoading,
    error: listError || detailError,
  };
}

function buildSelectedPlan(
  summaryPlan: SummaryPlanType | undefined,
  detailExplain: DetailExplainType,
  pctOfTotal: number,
): SelectedPlanType {
  if (!summaryPlan || !detailExplain) {
    return undefined;
  }
  const annotatedPlan = detailExplain.annotatedJson as AnnotatedPlan;
  const filteredAnnotations = annotatedPlan.annotations.filter((ann) =>
    ann.type.includes("query advisor"),
  );
  const enrichedPlan = enrichPlan(annotatedPlan.plan);
  const annotations = combineInsights(
    enrichedPlan,
    summaryPlan.insights,
    filteredAnnotations,
  );
  return {
    ...summaryPlan,
    plan: { plan: enrichedPlan, annotations },
    insightsWithRewrite: detailExplain.insightsWithRewrite,
    pctOfTotal: pctOfTotal ?? 0,
  };
}

function SummarizeInsightsCell({
  fieldData,
}: {
  fieldData: QueryTuningInsight[];
}) {
  const groupedByType = groupBy(fieldData, (i) => i.type);
  const entries = sortBy(
    Object.entries(groupedByType),
    ([_type, items]) => -items.length,
  );

  const typesAndCounts = entries.flatMap(([type, items]) => {
    const label = getAnnotationTitle(insightTypeToAnnotationType(type));
    if (items.length < 2) {
      return label;
    }
    return `${label} (${items.length})`;
  });

  return formatList(typesAndCounts, 3);
}

// Automatically select the first insight if nothing else is selected
function useSetDefaultSelectionOnLoad(planId: string, plan: AnnotatedPlan) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    setDone(false);
  }, [planId]);
  const { hash } = useLocation();
  const [initialHash] = useState(hash);
  const setSelectedAnnotationId = useSetSelectedAnnotationId({ replace: true });
  const annCount = plan?.annotations.length;
  const skip = done || !plan || annCount === 0 || initialHash !== "";

  useEffect(() => {
    if (skip) {
      return;
    }

    setSelectedAnnotationId(0);
  }, [skip, planId, setSelectedAnnotationId]);
}

function ActiveExplain({
  plan,
  databaseId,
  blockSize,
}: {
  plan: AnnotatedPlan;
  databaseId: string;
  blockSize: number;
}) {
  return (
    <ExplainGridView
      databaseId={databaseId}
      blockSize={blockSize}
      plan={plan}
    />
  );
}

function planWithInsightType(
  plans: PlanForInsightLookup[],
  plan: PlanForInsightLookup,
  type: string,
) {
  if (plan.insights.map((i) => `${i.type}-${i.id}`).includes(type)) {
    return plan;
  }
  return plans.find((p) =>
    p.insights.map((i) => `${i.type}-${i.id}`).includes(type),
  );
}

export const RewriteDescription = ({
  insight,
  insightType,
}: {
  insight: QueryTuningInsight;
  insightType: string;
}) => {
  const type = insightType.split("-")[0];
  switch (type) {
    case "NestedLoop":
      return <RewriteDescriptionNestedLoop />;
    case "OrderByLimit":
      return <RewriteDescriptionOrderByLimit insight={insight} />;
    case "WorkMemSpill":
      return <RewriteDescriptionWorkMemSpill insight={insight} />;
    case "InefficientIndexUse":
      return <RewriteDescriptionInefficientIndexUse insight={insight} />;
    case "OrToUnion":
      return <RewriteDescriptionOrToUnion insight={insight} />;
    default:
      return <div>Unable to rewrite this query.</div>;
  }
};

const RewriteDescriptionNestedLoop = () => {
  const [expandStep1, setExpandStep1] = useState(true);
  const [expandStep2, setExpandStep2] = useState(false);
  const [expandStep3, setExpandStep3] = useState(false);
  return (
    <div>
      <p>
        You can prevent PostgreSQL from repeatedly scanning the inner side of a
        Nested Loop by materializing it once with a MATERIALIZED CTE. This
        approach ensures the inner relation is evaluated a single time, avoiding
        redundant work when the outer table produces far more rows than
        expected. This also encourages the planner to use other join methods,
        such as Hash Join, that are typically more efficient for this query
        pattern.
      </p>
      <Button
        bare
        className="font-medium mb-2.5 w-full text-left"
        onClick={() => setExpandStep1((currExp) => !currExp)}
      >
        <ExpandToggle expanded={expandStep1} className="w-[20px]" />
        <FontAwesomeIcon icon={faCircle1} /> Find an inner table of Nested Loop
      </Button>
      {expandStep1 && (
        <p>
          From the below "EXPLAIN Plan" panel, find which table is labeled as
          "inner".
        </p>
      )}
      <Button
        bare
        className="font-medium mb-2.5 w-full text-left"
        onClick={() => setExpandStep2((currExp) => !currExp)}
      >
        <ExpandToggle expanded={expandStep2} className="w-[20px]" />
        <FontAwesomeIcon icon={faCircle2} /> Find a FROM clause using the inner
        table
      </Button>
      {expandStep2 && (
        <div className="mb-2.5">
          <p>
            Search the query, try to identify the part that inner table is a
            part of a FROM clause. It can be a subquery of an IN clause, or it
            can be a joined table.
          </p>
          <WorkbookDiff
            original=""
            modified={
              "SELECT * FROM tbl1\nWHERE col1 = $1 AND col2 IN (\n  SELECT id FROM inner_table\n  WHERE deleted_at IS NULL\n)"
            }
            options={[{ value: "read", label: "" }]}
            className="mb-2"
            isBaseline
          />
          <WorkbookDiff
            original=""
            modified={
              "SELECT * FROM tbl1\nJOIN inner_table\n  ON tbl1.col2 = inner_table.id\nWHERE tbl1.col1 = $1\n  AND inner_table.deleted_at IS NULL"
            }
            options={[{ value: "read", label: "" }]}
            isBaseline
          />
        </div>
      )}
      <Button
        bare
        className="font-medium mb-2.5 w-full text-left"
        onClick={() => setExpandStep3((currExp) => !currExp)}
      >
        <ExpandToggle expanded={expandStep3} className="w-[20px]" />
        <FontAwesomeIcon icon={faCircle3} /> Make a MATERIALIZED CTE with the
        inner table
      </Button>
      {expandStep3 && (
        <div className="mb-2.5">
          <p>
            For the case when the inner table is in a subquery of an IN clause,
            create a MATERIALIZED CTE with the WHERE conditions. Use that CTE
            instead inside of the IN clause.
          </p>
          <WorkbookDiff
            original=""
            modified={
              "WITH __inner_table AS MATERIALIZED (\n  SELECT id FROM inner_table\n  WHERE deleted_at IS NULL\n)\nSELECT * FROM tbl1\nWHERE col1 = $1 AND col2 IN (\n  SELECT id FROM __inner_table\n)"
            }
            options={[{ value: "read", label: "" }]}
            className="mb-2"
            isBaseline
          />
          <p>
            For the case when the inner table is a part of the joined table,
            also create a MATERIALIZED CTE with the WHERE conditions. Use that
            CTE for the join.
          </p>
          <WorkbookDiff
            original=""
            modified={
              "WITH __inner_table AS MATERIALIZED (\n  SELECT id FROM inner_table\n  WHERE deleted_at IS NULL\n)\nSELECT * FROM tbl1\nJOIN __inner_table\n  ON tbl1.col2 = __inner_table.id\nWHERE col1 = $1"
            }
            options={[{ value: "read", label: "" }]}
            isBaseline
          />
        </div>
      )}
    </div>
  );
};

const RewriteDescriptionOrderByLimit = ({
  insight,
}: {
  insight: QueryTuningInsight;
}) => {
  const [expandStep1, setExpandStep1] = useState(true);
  const [expandStep2, setExpandStep2] = useState(false);
  if (insight.type != "OrderByLimit") {
    return null;
  }
  return (
    <div>
      <p>
        You can force the planner to avoid using that inefficient index by
        adding +0 to the ORDER BY clause for the problematic column. This
        technique works because adding +0 to a column makes it an expression,
        preventing PostgreSQL from using a direct index on that column for
        sorting. The planner will consider the alternative execution strategies
        that may be more efficient for your specific query pattern.
      </p>
      <Button
        bare
        className="font-medium mb-2.5 w-full text-left"
        onClick={() => setExpandStep1((currExp) => !currExp)}
      >
        <ExpandToggle expanded={expandStep1} className="w-[20px]" />
        <FontAwesomeIcon icon={faCircle1} /> Find ORDER BY clause and a column
      </Button>
      {expandStep1 && (
        <div className="mb-2.5">
          <p>
            Find the ORDER BY clause this insight detected. Then, find a column
            in the ORDER BY clause that is using the inefficient index.
          </p>
          <p>
            The name of the wrong index: <code>{insight["index_used"]}</code>
          </p>
        </div>
      )}
      <Button
        bare
        className="font-medium mb-2.5 w-full text-left"
        onClick={() => setExpandStep2((currExp) => !currExp)}
      >
        <ExpandToggle expanded={expandStep2} className="w-[20px]" />
        <FontAwesomeIcon icon={faCircle2} /> Add +0 to the column
      </Button>
      {expandStep2 && (
        <div className="mb-2.5">
          <p>
            Add +0 to that found column. Note that this only works if the column
            type is bigint, int, smallint.
          </p>
          <p>Below is the example of how to modify it:</p>
          <div className="p-2 bg-[#f5f5f8] rounded border border-[#E8E8EE] text-[#606060]">
            <SQL
              sql={
                "SELECT * FROM tbl1\nWHERE category = 'test'\nORDER BY id + 0 LIMIT 100"
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

const RewriteDescriptionWorkMemSpill = ({
  insight,
}: {
  insight: QueryTuningInsight;
}) => {
  const [expandStep1, setExpandStep1] = useState(true);
  const [expandStep2, setExpandStep2] = useState(false);
  const [expandStep3, setExpandStep3] = useState(false);
  if (insight.type !== "WorkMemSpill") {
    return null;
  }

  const { current_bytes, needed_bytes, maximum_bytes, node_type } = insight;
  const targetBytes = maximum_bytes ?? needed_bytes;
  const targetMb = targetBytes
    ? Math.max(Math.round(targetBytes / (1024 * 1024)), 1)
    : null;
  const targetLabel = targetMb ? `${targetMb}MB` : "<desired value>";
  const isHash = node_type === "Hash";
  const hasStats =
    current_bytes != null || needed_bytes != null || maximum_bytes != null;

  return (
    <div>
      <p>
        When a Sort or Hash operation doesn't fit within <code>work_mem</code>,
        Postgres writes intermediate data to temporary files on disk, which is
        much slower than in-memory processing. Raising <code>work_mem</code> can
        avoid spilling to disk and reduce query overhead.
      </p>
      {hasStats && (
        <p className="mt-2">
          {current_bytes != null && (
            <>
              The current <code>work_mem</code> for this server is{" "}
              <strong>{formatBytes(current_bytes)}</strong>.{" "}
            </>
          )}
          {needed_bytes != null && (
            <>
              This node wrote a <strong>{formatBytes(needed_bytes)}</strong>{" "}
              temporary file to disk due to insufficient <code>work_mem</code>.{" "}
            </>
          )}
          {maximum_bytes != null && (
            <>
              For this server, <code>work_mem</code> can safely go up to{" "}
              <strong>{formatBytes(maximum_bytes)}</strong>. This ceiling is
              based on the server's memory, <code>shared_buffers</code>, and
              typical active connection count, leaving headroom for normal load.
            </>
          )}
        </p>
      )}
      {isHash && (
        <p className="mt-2">
          For hash operations specifically, raising{" "}
          <code>hash_mem_multiplier</code> (Postgres 13+, default 2.0) is an
          alternative that gives hash operations more effective memory without
          affecting sorts.
        </p>
      )}
      <p className="mt-2">
        You can apply this change at several scopes, from a single session or
        query to a persistent setting on a database, role, or the whole server.
      </p>
      <Button
        bare
        className="font-medium mb-2.5 w-full text-left"
        onClick={() => setExpandStep1((currExp) => !currExp)}
      >
        <ExpandToggle expanded={expandStep1} className="w-[20px]" />
        <FontAwesomeIcon icon={faCircle1} /> Temporarily, in psql
      </Button>
      {expandStep1 && (
        <div className="mb-2.5">
          <p>
            Change <code>work_mem</code> for the current session. Useful for
            ad-hoc investigation, batch jobs, or migrations without altering the
            server-wide default. You can also set <code>work_mem</code> in the
            planner settings of a workbook for this query to compare results
            before and after.
          </p>
          <div className="p-2 bg-[#f5f5f8] rounded border border-[#E8E8EE] text-[#606060]">
            <SQL
              sql={`SET work_mem = '${targetLabel}';\nSELECT ...\nRESET work_mem;`}
            />
          </div>
        </div>
      )}
      <Button
        bare
        className="font-medium mb-2.5 w-full text-left"
        onClick={() => setExpandStep2((currExp) => !currExp)}
      >
        <ExpandToggle expanded={expandStep2} className="w-[20px]" />
        <FontAwesomeIcon icon={faCircle2} /> Per query, using{" "}
        <code>pg_hint_plan</code>
      </Button>
      {expandStep2 && (
        <div className="mb-2.5">
          <p>
            If the <code>pg_hint_plan</code> extension is installed, you can
            raise <code>work_mem</code> for just this statement by prefixing the
            query with a <code>Set</code> hint comment. This doesn't affect any
            other query.
          </p>
          <div className="p-2 bg-[#f5f5f8] rounded border border-[#E8E8EE] text-[#606060]">
            <SQL sql={`/*+ Set(work_mem "${targetLabel}") */\nSELECT ...`} />
          </div>
        </div>
      )}
      <Button
        bare
        className="font-medium mb-2.5 w-full text-left"
        onClick={() => setExpandStep3((currExp) => !currExp)}
      >
        <ExpandToggle expanded={expandStep3} className="w-[20px]" />
        <FontAwesomeIcon icon={faCircle3} /> Persistently, scoped to a database,
        role, or the whole server
      </Button>
      {expandStep3 && (
        <div className="mb-2.5">
          <p>
            For a more permanent change, you can scope <code>work_mem</code> to
            a specific database or role. This is useful when only some workloads
            need the higher value:
          </p>
          <div className="p-2 bg-[#f5f5f8] rounded border border-[#E8E8EE] text-[#606060]">
            <SQL
              sql={`ALTER DATABASE mydb SET work_mem = '${targetLabel}';\n-- or\nALTER ROLE myrole SET work_mem = '${targetLabel}';`}
            />
          </div>
          <p className="!mt-2">
            To raise the server-wide default instead, update{" "}
            <code>work_mem</code> in your Postgres configuration. This is the
            parameter group / configuration UI provided by your managed Postgres
            service, or <code>postgresql.conf</code> for self-managed servers.
            Reload the configuration after the change.
          </p>
        </div>
      )}
    </div>
  );
};

const RewriteDescriptionInefficientIndexUse = ({
  insight,
}: {
  insight: QueryTuningInsight;
}) => {
  const [expandStep1, setExpandStep1] = useState(true);
  const [expandStep2, setExpandStep2] = useState(false);
  if (insight.type !== "InefficientIndexUse") {
    return null;
  }

  const filterColumns = insight.filter_columns ?? [];
  const filterColumnsLabel =
    filterColumns.length > 0 ? filterColumns.join(", ") : "<filter columns>";

  const proposedIndexNames = insight.proposed_index_names ?? [];
  const tableLabel = insight.table_alias ?? "<table>";
  const indexLabel = proposedIndexNames[0] ?? "<better_index>";

  return (
    <div>
      <p>
        The planner picked an index that doesn't match the filter conditions of
        this query, so Postgres reads many rows from the index and then discards
        most of them. A different existing index on the filter column(s) could
        let the scan return fewer rows.
      </p>
      <p>
        One way to address this is to force a specific index using a{" "}
        <code>pg_hint_plan</code> <code>IndexScan</code> hint, covered in the
        steps below.
      </p>
      <Button
        bare
        className="font-medium mb-2.5 w-full text-left"
        onClick={() => setExpandStep1((currExp) => !currExp)}
      >
        <ExpandToggle expanded={expandStep1} className="w-[20px]" />
        <FontAwesomeIcon icon={faCircle1} /> Identify a better index
      </Button>
      {expandStep1 && (
        <div className="mb-2.5">
          <p>
            The planner picked <code>{insight["index_used"]}</code>, but the
            filter conditions of this query (<code>{filterColumnsLabel}</code>)
            aren't covered by that index, so Postgres reads many rows and
            discards them at the Filter step.
          </p>
          {proposedIndexNames.length > 0 ? (
            <>
              <p>
                Alternative indexes on this table that could be used instead:
              </p>
              <ul className="list-disc pl-6 mb-2">
                {proposedIndexNames.map((n) => (
                  <li key={n}>
                    <code>{n}</code>
                  </li>
                ))}
              </ul>
              <p>
                Whether one of these is faster than the current choice depends
                on how selective your filter values are in practice — try the
                hint in step 2 on a sample of your workload to confirm.
              </p>
            </>
          ) : (
            <p>
              Look for another existing index on this table that covers the
              filter columns above. A composite or partial index targeting those
              columns can help the planner skip the Filter step.
            </p>
          )}
        </div>
      )}
      <Button
        bare
        className="font-medium mb-2.5 w-full text-left"
        onClick={() => setExpandStep2((currExp) => !currExp)}
      >
        <ExpandToggle expanded={expandStep2} className="w-[20px]" />
        <FontAwesomeIcon icon={faCircle2} /> Force the better index with{" "}
        <code>pg_hint_plan</code>
      </Button>
      {expandStep2 && (
        <div className="mb-2.5">
          <p>
            Prefix the query with an <code>IndexScan</code> hint to pin the scan
            to a specific index. This affects only this statement.
          </p>
          {proposedIndexNames.length > 0 && (
            <>
              <p>
                Pick any of the alternatives and plug it into the hint below:
              </p>
              <ul className="list-disc pl-6 mb-2">
                {proposedIndexNames.map((n) => (
                  <li key={n}>
                    <code>{n}</code>
                  </li>
                ))}
              </ul>
              <p>
                Example using <code>{indexLabel}</code>:
              </p>
            </>
          )}
          <div className="p-2 bg-[#f5f5f8] rounded border border-[#E8E8EE] text-[#606060]">
            <SQL
              sql={`/*+ IndexScan(${tableLabel} ${indexLabel}) */\nSELECT ...`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const RewriteDescriptionOrToUnion = ({
  insight,
}: {
  insight: QueryTuningInsight;
}) => {
  const [expandStep1, setExpandStep1] = useState(true);
  const [expandStep2, setExpandStep2] = useState(false);
  if (insight.type !== "OrToUnion") {
    return null;
  }

  const columns = insight.split_by_columns ?? [];
  const columnLabels = columns.map((c) =>
    c.table_refname ? `${c.table_refname}.${c.column}` : c.column,
  );
  const tableNames = columns.map((c) => c.table_refname);
  const tableA = tableNames[0] ?? "a";
  const tableB = tableNames[1] ?? "b";

  const beforeSql = `SELECT ...\nFROM ${tableA}\nJOIN ${tableB} ON ...\nWHERE <condition on ${tableA}> OR <condition on ${tableB}>`;
  const afterSql = `SELECT ...\nFROM ${tableA}\nJOIN ${tableB} ON ...\nWHERE <condition on ${tableA}>\nUNION\nSELECT ...\nFROM ${tableA}\nJOIN ${tableB} ON ...\nWHERE <condition on ${tableB}>`;

  return (
    <div>
      <p>
        This join combines conditions with <code>OR</code>, and each side of the{" "}
        <code>OR</code> filters a different table. Because a single{" "}
        <code>OR</code> references both tables, neither condition can be pushed
        down to an index scan on its own table. Postgres first reads the data
        without using those conditions and only applies the <code>OR</code>{" "}
        after joining, as a <code>Join Filter</code>, so each side reads far
        more rows than it needs to. Splitting the query into a{" "}
        <code>UNION</code> lets each side utilize an index scan with one part of
        the OR condition.
      </p>
      <Button
        bare
        className="font-medium mb-2.5 w-full text-left"
        onClick={() => setExpandStep1((currExp) => !currExp)}
      >
        <ExpandToggle expanded={expandStep1} className="w-[20px]" />
        <FontAwesomeIcon icon={faCircle1} /> Identify the OR that spans the join
      </Button>
      {expandStep1 && (
        <div className="mb-2.5">
          <p>The join filter that combines both tables is:</p>
          <div className="p-2 bg-[#f5f5f8] rounded border border-[#E8E8EE] text-[#606060] mb-2">
            <SQL sql={insight.join_filter} />
          </div>
          {columnLabels.length > 0 && (
            <p>
              Each side references a different table, on{" "}
              {columnLabels.map((label, i) => (
                <React.Fragment key={label}>
                  {i > 0 && ", "}
                  <code>{label}</code>
                </React.Fragment>
              ))}
              . Splitting on these conditions is what lets each side use an
              index.
            </p>
          )}
        </div>
      )}
      <Button
        bare
        className="font-medium mb-2.5 w-full text-left"
        onClick={() => setExpandStep2((currExp) => !currExp)}
      >
        <ExpandToggle expanded={expandStep2} className="w-[20px]" />
        <FontAwesomeIcon icon={faCircle2} /> Rewrite the query as a{" "}
        <code>UNION</code>
      </Button>
      {expandStep2 && (
        <div className="mb-2.5">
          <p>
            Turn each side of the <code>OR</code> into its own{" "}
            <code>SELECT</code> over the same join, then combine them. Starting
            from a query shaped like this:
          </p>
          <div className="p-2 bg-[#f5f5f8] rounded border border-[#E8E8EE] text-[#606060] mb-2">
            <SQL sql={beforeSql} />
          </div>
          <p>rewrite it as:</p>
          <div className="p-2 bg-[#f5f5f8] rounded border border-[#E8E8EE] text-[#606060] mb-2">
            <SQL sql={afterSql} />
          </div>
          <p>
            Use <code>UNION</code> rather than <code>UNION ALL</code> so rows
            matching both conditions aren't returned twice. Confirm the rewrite
            keeps the same results and check that each side uses an index before
            adopting it.
          </p>
        </div>
      )}
    </div>
  );
};
